import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import { toast } from "sonner";
import { useAuth } from '@/App';
const WardenAssignment = () => {
  const {user} = useAuth();
  const [students, setStudents] = useState([]);
  const [wardens, setWardens] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedWarden, setSelectedWarden] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({
    hasWarden: 'all',
    searchName: ''
  });

  useEffect(() => {
    fetchWardens();
    fetchStudents();
  }, []);

  const fetchWardens = async () => {
    try {
      const response = await apiClient.get(`/v1/admin/wardens/?hostel_id=${user.hostel_id}`);
      setWardens(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Error fetching wardens:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const params = {hostel_id: user.hostel_id};
      if (filters.hasWarden !== 'all') {
        params.has_warden = filters.hasWarden === 'yes';
      }
      if (filters.searchName) {
        params.search_name = filters.searchName;
      }

      const response = await apiClient.get('/v1/admin/students/', { params });
      setStudents(response.data || []);
    } catch (error) {
      toast.error('Error fetching students:', error);
    }
  };

  const handleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  const handleAssignWarden = async () => {
    if (!selectedWarden || selectedStudents.length === 0) {
      setMessage({ type: 'error', text: 'Please select a warden and at least one student' });
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/v1/admin/students/assign-warden', {
        student_ids: selectedStudents,
        warden_id: selectedWarden
      });
      console.log({
        student_ids: selectedStudents,
        warden_id: selectedWarden
      });
      

      
      setSelectedStudents([]);
      fetchStudents();
    } catch (error) {
      setMessage({ type: 'error', text:  'Failed to assign warden' });
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignWarden = async () => {
    if (selectedStudents.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one student' });
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/v1/admin/students/unassign-warden', selectedStudents);
      setMessage({ type: 'success', text: response.data.message });
      setSelectedStudents([]);
      fetchStudents();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to unassign warden' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Warden Assignment</h1>

      {message.text && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Warden Assignment Status
            </label>
            <select
              className="w-full p-2 border border-gray-300 rounded-lg"
              value={filters.hasWarden}
              onChange={(e) => {
                setFilters({ ...filters, hasWarden: e.target.value });
              }}
            >
              <option value="all">All Students</option>
              <option value="yes">With Warden</option>
              <option value="no">Without Warden</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search by Name
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-lg"
              placeholder="Enter student name"
              value={filters.searchName}
              onChange={(e) => setFilters({ ...filters, searchName: e.target.value })}
            />
          </div>
        </div>
        <button
          onClick={fetchStudents}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Apply Filters
        </button>
      </div>

      {/* Assignment Controls */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Assign Warden</h2>
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Warden
            </label>
            <select
              className="w-full p-2 border border-gray-300 rounded-lg"
              value={selectedWarden}
              onChange={(e) => setSelectedWarden(e.target.value)}
            >
              <option value="">Choose a warden...</option>
              {wardens.map(warden => (
                <option key={warden.id} value={warden.id}>
                  {warden.name} ({warden.assigned_student_count || 0} students)
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleAssignWarden}
            disabled={loading || selectedStudents.length === 0}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
          >
            Assign ({selectedStudents.length})
          </button>
          <button
            onClick={handleUnassignWarden}
            disabled={loading || selectedStudents.length === 0}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
          >
            Unassign
          </button>
        </div>
      </div>

      {/* Students List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Students ({students?.length})</h2>
            <button
              onClick={handleSelectAll}
              className="text-blue-600 hover:text-blue-800"
            >
              {selectedStudents.length === students?.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <input
                      type="checkbox"
                      checked={selectedStudents.length === students.length && students.length > 0}
                      onChange={handleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Room</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Warden</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {students.map(student => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student.id)}
                        onChange={() => handleStudentSelection(student.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">{student.readable_id || student.id}</td>
                    <td className="px-4 py-3 text-sm font-medium">{student.name}</td>
                    <td className="px-4 py-3 text-sm">{student.class_name}</td>
                    <td className="px-4 py-3 text-sm">{student.room_number}</td>
                    <td className="px-4 py-3 text-sm">
                      {student.warden_name ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                          {student.warden_name}
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded">
                          Not Assigned
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {students.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No students found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WardenAssignment;